import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Download, Printer, Eye, Edit, Trash2, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { purchasesAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';

const PurchaseInvoicesList = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadInvoices();
  }, [statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const response = await purchasesAPI.listInvoices(params);
      setInvoices(response.data?.invoices || response.data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Failed to load purchase invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    const businessPath = activeBusiness?.id ? `/business/${activeBusiness.id}` : '';
    navigate(`${businessPath}/purchases/invoices/create`);
  };

  const handleEdit = (id) => {
    const businessPath = activeBusiness?.id ? `/business/${activeBusiness.id}` : '';
    navigate(`${businessPath}/purchases/invoices/${id}/edit`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'premium-status-neutral',
      FINALIZED: 'premium-status-info',
      PAID: 'premium-status-success',
      PARTIAL: 'premium-status-warning',
      OVERDUE: 'premium-status-danger',
      CANCELLED: 'premium-status-neutral'
    };
    return colors[status] || 'premium-status-neutral';
  };

  const filteredInvoices = invoices.filter(inv =>
    searchTerm === '' ||
    (inv.invoice_number || inv.doc_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (inv.party_name || inv.party?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading purchase invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f9fafb 0%, #fef3c7 50%, #fde68a 100%)' }}>
      {/* Header */}
      <div className="sticky top-0 z-20 border-b shadow-lg px-3 sm:px-4 md:px-6 py-3 sm:py-4" style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
        backdropFilter: 'blur(12px)'
      }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between max-w-[1920px] mx-auto gap-3">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold" style={{
              background: 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Purchase Invoices
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Manage supplier invoices and bills</p>
          </div>
          <Button
            onClick={handleCreate}
            className="w-full sm:w-auto"
            size="sm"
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Purchase Invoice
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 sm:px-4 md:px-6 py-4 sm:py-6 max-w-[1920px] mx-auto">
        {/* Filters & Search */}
        <Card className="p-3 sm:p-4 mb-4 sm:mb-6 border-2" style={{ borderColor: '#fde68a' }}>
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-2.5 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <Input
                placeholder="Search by invoice number or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 sm:pl-11 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {['ALL', 'DRAFT', 'FINALIZED', 'PAID', 'OVERDUE'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    statusFilter === status
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Invoices Grid - PREMIUM POLISH */}
        {filteredInvoices.length === 0 ? (
          <div className="premium-empty-state">
            <FileText className="premium-empty-icon" />
            <h3 className="premium-empty-title">No purchase invoices found</h3>
            <p className="premium-empty-description">Start tracking your supplier invoices and manage your purchases efficiently</p>
            <Button onClick={handleCreate} className="premium-button-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Purchase Invoice
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredInvoices.map(invoice => (
              <div key={invoice.id} className="premium-card p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-base text-gray-900">{invoice.invoice_number || invoice.doc_no || 'N/A'}</h3>
                    <p className="premium-meta mt-0.5">{invoice.party_name || invoice.party?.name || 'Unknown Supplier'}</p>
                  </div>
                  <span className={`premium-status ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="premium-meta">Date:</span>
                    <span className="font-medium text-gray-900">{new Date(invoice.invoice_date || invoice.doc_date).toLocaleDateString('en-IN')}</span>
                  </div>
                  {invoice.due_date && (
                    <div className="flex justify-between text-sm">
                      <span className="premium-meta">Due:</span>
                      <span className="font-medium text-gray-900">{new Date(invoice.due_date).toLocaleDateString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                    <span className="font-semibold text-gray-700">Total:</span>
                    <span className="font-bold text-lg text-gray-900">{formatCurrency(invoice.grand_total ?? invoice.totals?.grand_total)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => handleEdit(invoice.id)} className="premium-icon-button flex-1 justify-center py-2">
                    <Edit className="w-4 h-4 mr-1" />
                    <span className="text-sm">Edit</span>
                  </button>
                  <button className="premium-icon-button flex-1 justify-center py-2">
                    <Eye className="w-4 h-4 mr-1" />
                    <span className="text-sm">View</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseInvoicesList;
